import { env } from '@/shared/config/env';
import { ApiError } from './apiError';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  baseUrl?: string;
  isFormData?: boolean;
  skipAuthRefresh?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

function refreshAccessToken(): Promise<boolean> {
    if (refreshPromise) return refreshPromise;

    refreshPromise = fetch(`${env.apiBaseUrl}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, baseUrl = env.apiBaseUrl, isFormData, headers, skipAuthRefresh = false, ...rest } = options;

  let requestBody: BodyInit | undefined;
  const requestHeaders: HeadersInit = { Accept: 'application/json', ...headers };

  if (body instanceof FormData) {
    requestBody = body;
  } else if (isFormData) {
    requestBody = body as BodyInit;
  } else if (body !== undefined) {
    requestBody = JSON.stringify(body);
    (requestHeaders as Record<string, string>)['Content-Type'] = 'application/json';
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      credentials: 'include',
      ...rest,
      headers: requestHeaders,
      body: requestBody,
    });
  } catch {
    throw ApiError.network();
  }
  const canRefresh = response.status == 401 && !skipAuthRefresh && baseUrl == env.apiBaseUrl && !path.startsWith('/auth/')
  && !path.startsWith('/admin/');
  if (canRefresh){
      const refreshed = await refreshAccessToken();

      if (refreshed){
          return request<T>(path, {
              ...options, skipAuthRefresh: true});
              }
          }
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('application/json') ? await response.json().catch(() => null) : null;

  if (!response.ok) {
  const errorPayload = payload as {
    message?: string;
    detail?: string;
    code?: string;
  } | null;

  const message =
    errorPayload?.message ??
    errorPayload?.detail ??
    response.statusText;

  throw new ApiError(message, response.status, undefined, errorPayload?.code);
}

  return payload as T;
}

export const httpClient = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};

export const placesHttpClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET', baseUrl: env.placesApiBaseUrl }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body, baseUrl: env.placesApiBaseUrl }),
};
